import { createReactBlockSpec } from "@blocknote/react";
import styled from "@emotion/styled";
import { Link, StyleSheet, Text as PdfText, View } from "@react-pdf/renderer";
import { ExternalHyperlink, Paragraph, TextRun } from "docx";
import { lightTheme } from "styles/theme";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { routes } from "lib/routes";
import type { StotlesPartialBlock } from "../types";
import { DocxUIcon } from "./docxUtils";
import { PdfUIcon } from "./pdfUtils";

function getCalloutTitle(orgName: string, userCompanyName: string): string {
  return `Our assessment of ${orgName} for ${userCompanyName}`;
}
function getOrgProfileHref(orgGuid: string, orgType: CalloutOrgType): string {
  return orgType === "supplier" ? `${routes.supplier}/${orgGuid}` : `${routes.buyer}/${orgGuid}`;
}
function getOrgProfileCta(orgType: CalloutOrgType): string {
  return orgType === "supplier" ? "Explore the supplier profile" : "Explore the buyer profile";
}

const CalloutOrgTypeValues = ["buyer", "supplier"] as const;
export type CalloutOrgType = (typeof CalloutOrgTypeValues)[number];

export type StotlesCalloutBlockProps = {
  orgGuid: string;
  orgName: string;
  orgType: CalloutOrgType;
  userCompanyName: string;
  body: string;
};

export const stotlesCalloutBlockSpec = createReactBlockSpec(
  {
    type: "stotlesCallout",
    propSchema: {
      orgGuid: {
        default: undefined, // Required
        type: "string",
      },
      orgName: {
        default: undefined, // Required
        type: "string",
      },
      orgType: {
        default: "buyer", // Required
        values: CalloutOrgTypeValues,
      },
      userCompanyName: {
        default: undefined, // Required
        type: "string",
      },
      body: {
        default: undefined, // Required
        type: "string",
      },
    },
    content: "none",
  },
  {
    render: ({ block }) => {
      const { orgGuid, orgName, orgType, userCompanyName, body } = block.props;
      const orgProfileHref = getOrgProfileHref(orgGuid, orgType);

      return (
        <Container
          data-testid="stotles-callout-block"
          aria-label="Stotles callout block"
          column
          gap={12}
          width="100%"
        >
          <Flex gap={8}>
            <UIcon icon="stotles" />
            <Text h2>{getCalloutTitle(orgName, userCompanyName)}</Text>
          </Flex>
          <Text>{body}</Text>
          <Button
            icon="arrowUpRight"
            buttonType="secondary"
            as="a"
            href={orgProfileHref}
            width="fit-content"
          >
            {getOrgProfileCta(orgType)}
          </Button>
        </Container>
      );
    },
  },
);

const Container = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.sysPrimarySubtle,
  borderLeft: `3px solid ${theme.colors.sysPrimaryDefault}`,
  padding: "16px",
}));

export const createStotlesCalloutBlock = ({
  orgName,
  userCompanyName,
  body,
  orgGuid,
  orgType = "buyer",
}: StotlesCalloutBlockProps): StotlesPartialBlock => ({
  type: "stotlesCallout",
  props: {
    orgName,
    userCompanyName,
    body,
    orgGuid,
    orgType,
  },
});

export const stotlesCalloutBlockDocxMapping = async ({
  orgGuid,
  orgName,
  orgType,
  userCompanyName,
  body,
}: StotlesCalloutBlockProps) => {
  return new Paragraph({
    children: [
      await DocxUIcon({ icon: "stotles" }),
      new TextRun({
        text: getCalloutTitle(orgName, userCompanyName),
        bold: true,
      }),
      // Some docx renders (Office Online, Google Docs, LibreOffice) ignore \n line breaks, so need to manually handle these
      ...body.split("\n").flatMap((line) => [
        new TextRun({
          text: line,
          break: 1,
        }),
      ]),
      new ExternalHyperlink({
        children: [
          new TextRun({
            text: getOrgProfileCta(orgType),
            color: lightTheme.colors.sysPrimaryDefault.replace("#", ""),
            underline: {}, // Enable underline
            break: 1,
          }),
        ],
        link: `${window.location.origin}${getOrgProfileHref(orgGuid, orgType)}`,
      }),
    ],
    border: {
      left: {
        style: "single",
        color: lightTheme.colors.sysPrimaryDefault.replace("#", ""),
        size: 16,
        space: 8,
      },
      right: {
        style: "none",
        space: 8,
      },
      top: {
        style: "none",
        space: 8,
      },
      bottom: {
        style: "none",
        space: 8,
      },
    },
    shading: {
      fill: lightTheme.colors.sysPrimarySubtle.replace("#", ""),
    },
  });
};

const pdfStyles = StyleSheet.create({
  container: {
    borderLeft: `3px solid ${lightTheme.colors.sysPrimaryDefault}`,
    backgroundColor: lightTheme.colors.sysPrimarySubtle,
    paddingLeft: "8px",
    paddingRight: "4px",
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  headerContainer: { display: "flex", flexDirection: "row", gap: "8px" },
  headerText: { fontWeight: "bold", width: "100%" },
  link: {
    color: lightTheme.colors.sysPrimaryDefault,
    textDecoration: "underline",
  },
});

export const stotlesCalloutBlockPdfMapping = ({
  orgGuid,
  orgName,
  orgType,
  userCompanyName,
  body,
}: StotlesCalloutBlockProps) => {
  return (
    <View style={pdfStyles.container}>
      <View style={pdfStyles.headerContainer}>
        <PdfUIcon icon="stotles" />
        <PdfText style={pdfStyles.headerText}>{getCalloutTitle(orgName, userCompanyName)}</PdfText>
      </View>
      <PdfText>{body}</PdfText>
      <Link
        href={`${window.location.origin}${getOrgProfileHref(orgGuid, orgType)}`}
        style={pdfStyles.link}
      >
        {getOrgProfileCta(orgType)}
      </Link>
    </View>
  );
};
